/* Etat + logique du chat Naïa d'un document (content-editor + page de
   structuration post-import, spec 11) — extrait de content-editor.tsx pour
   être partagé sans duplication (DRY). */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import {
  streamTeacherChat, exhaustedMessage, RESUME_MESSAGE, RETRY_AFTER_WRITE_MESSAGE,
  type TeacherMsg, type DocumentContext, type ContentDocumentKind, type TeacherChatAttachment,
} from '../lib/editor-chat';
import type { SectionUpdatedEvent, SectionCreatedEvent, SectionsReorderedEvent } from '../lib/chat-api';
import { composerKeyHandler } from '../lib/composer-keys';
import { chatErrorMessage } from '../lib/chat-errors';

export interface PendingImport {
  id: string; storage_path: string; mime_type: string; note: string;
  variant: 'subject' | 'answer_key'; signed_read_url: string;
}

export interface UseDocumentChatOptions {
  documentId: string;
  doc: { title: string; level: string; subject: string; kind: ContentDocumentKind } | null;
  /** Le bootstrap démarre un travail de fond hors du flux SSE — le consommateur
   *  (ex. content-editor) décide comment en suivre l'avancement. */
  onBootstrapStarted?: () => void;
  /** Page de structuration post-import (spec 11) — prompt système différent,
   *  jamais l'édition libre de content-editor.tsx. */
  reviewMode?: boolean;
  /** Variante actuellement affichée à l'écran (lot 3, spec 13) — lue une seule
   *  fois par appel à `send()`/`sendText()`, donc figée au moment de l'envoi.
   *  `null`/absent = variante de référence. */
  activeVariantKey?: string | null;
}

export function useDocumentChat({ documentId, doc, onBootstrapStarted, reviewMode, activeVariantKey }: UseDocumentChatOptions) {
  const id = documentId;
  const [incomingSection, setIncomingSection] = useState<SectionUpdatedEvent | null>(null);
  const [incomingCreated, setIncomingCreated] = useState<SectionCreatedEvent | null>(null);
  const [incomingReordered, setIncomingReordered] = useState<SectionsReorderedEvent | null>(null);
  /* Signal générique `resource_changed` (harmonisation backend-first, 28/09) :
     incrémenté à chaque événement concernant CE document, pour déclencher un
     refetch ciblé côté consommateur sans jamais porter la donnée elle-même. */
  const [resourceChangedTick, setResourceChangedTick] = useState(0);

  const [messages, setMessages] = useState<TeacherMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatErrorIsPartialWrite, setChatErrorIsPartialWrite] = useState(false);
  const [exhaustedNotice, setExhaustedNotice] = useState<string | null>(null);
  const [truncatedNotice, setTruncatedNotice] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const lastUserMessageRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  /* Restore the sidechat thread + history so a reload keeps the conversation. */
  useEffect(() => {
    api.content.thread.query({ documentId: id })
      .then((t) => { if (t) { setThreadId(t.id); setMessages(t.messages as TeacherMsg[]); } })
      .catch(() => {});
  }, [id]);

  /* Import fait dans la modale « Créer et améliorer », en attente d'être envoyé
     au chat. Rappelé APRÈS chaque consommation : un examen peut porter un sujet
     ET un corrigé en attente, le second doit apparaître une fois le premier
     traité — sinon le corrigé reste invisible et l'import semble perdu. */
  const loadPendingImport = useCallback(() => {
    api.content.imports.pending.query({ documentId: id })
      .then((imp) => {
        if (!imp) return;
        setPendingImport(imp);
        setDraft((prev) => prev || imp.note);
      })
      .catch(() => {});
  }, [id]);
  useEffect(() => { loadPendingImport(); }, [loadPendingImport]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, streamText]);

  const sendText = async (
    text: string,
    attachment?: TeacherChatAttachment | null,
    importVariant?: 'subject' | 'answer_key' | null,
  ) => {
    if ((!text && !attachment) || streaming) return;
    setStreaming(true); setStreamText(''); setChatError(null); setChatErrorIsPartialWrite(false);
    setExhaustedNotice(null); setTruncatedNotice(false);
    lastUserMessageRef.current = text;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    const consumedImportId = pendingImport?.id ?? null;
    if (attachment) setPendingImport(null);
    const ab = new AbortController();
    abortRef.current = ab;
    const documentContext: DocumentContext | null = doc
      ? { title: doc.title, level: doc.level, subject: doc.subject, kind: doc.kind }
      : null;
    let full = '';
    try {
      await streamTeacherChat(text, threadId, {
        onDelta: (t) => { full += t; setStreamText(full); },
        onThread: (tid) => setThreadId(tid),
        onSectionUpdated: (s) => setIncomingSection(s as SectionUpdatedEvent),
        onSectionCreated: (s) => setIncomingCreated(s as SectionCreatedEvent),
        onSectionsReordered: (ev) => setIncomingReordered(ev as SectionsReorderedEvent),
        onResourceChanged: (ev) => { if (ev.resource === 'content' && ev.id === id) setResourceChangedTick((t) => t + 1); },
        /* Le bootstrap ne rend plus de sections : il démarre un travail de
           fond — le consommateur suit son avancement via useBootstrapJob. */
        onBootstrapStarted: () => { onBootstrapStarted?.(); },
        /* Un tour sans prose ne doit pas laisser une bulle vide dans le fil : le
           serveur ne fabrique plus de '(pas de texte)' pour la remplir. */
        onDone: (t) => { if (t) setMessages((prev) => [...prev, { role: 'assistant', content: t }]); setStreamText(''); },
        onExhausted: (e) => setExhaustedNotice(exhaustedMessage(e.writes)),
        onTruncated: () => setTruncatedNotice(true),
        /* Le serveur a retiré un texte déjà diffusé : on nettoie l'écran avant la relance. */
        onRetract: () => { full = ''; setStreamText(''); },
        onError: (err) => {
          if (err.message === '__write_partial__') {
            setChatErrorIsPartialWrite(true);
            setChatError('La connexion a été interrompue après une modification partielle du document.');
          } else {
            setChatError(err.message);
          }
        },
      }, ab.signal, id, documentContext, attachment ? [attachment] : null, importVariant ?? null, reviewMode, activeVariantKey ?? null);
      if (consumedImportId) {
        await api.content.imports.consume.mutate({ documentId: id, importId: consumedImportId }).catch(() => {});
        loadPendingImport();
      }
    } catch (err) {
      /* Lève AVANT que le flux SSE ne démarre (auth, validation, panne non
         gérée côté serveur) : contrairement à `onError` ci-dessus, rien n'a
         encore nettoyé le message pour l'utilisateur — ne jamais l'afficher
         brut (cause de la capture Yassine 14/10 : un 500 générique montrait
         le JSON `{"error":{...}}` tel quel). */
      if ((err as Error).name !== 'AbortError') setChatError(chatErrorMessage(err));
    } finally { setStreaming(false); }
  };

  const retryLastMessage = async () => {
    const text = lastUserMessageRef.current;
    if (!text || streaming) return;
    if (chatErrorIsPartialWrite) {
      await sendText(RETRY_AFTER_WRITE_MESSAGE);
      return;
    }
    setChatError(null); setChatErrorIsPartialWrite(false);
    setMessages((prev) => prev.filter((m, i) => !(i === prev.length - 1 && m.role === 'user' && m.content === text)));
    await sendText(text);
  };

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if ((!text && !pendingImport) || streaming) return;
    setDraft('');
    const attachment: TeacherChatAttachment | null = pendingImport
      ? { url: pendingImport.signed_read_url, mime: pendingImport.mime_type, storagePath: pendingImport.storage_path }
      : null;
    await sendText(text, attachment, pendingImport?.variant ?? null);
  };
  const handleKey = composerKeyHandler(() => void send({ preventDefault: () => {} } as FormEvent));

  return {
    incomingSection, incomingCreated, incomingReordered, resourceChangedTick,
    messages, draft, setDraft, pendingImport, clearPendingImport: () => setPendingImport(null),
    streaming, streamText, chatError, chatErrorIsPartialWrite,
    exhaustedNotice, truncatedNotice,
    onContinue: () => { void sendText(RESUME_MESSAGE); },
    scrollRef, handleKey, send, sendText, retryLastMessage,
    refreshPendingImport: loadPendingImport,
  };
}
